// Country phone codes mapping
export const phoneCodes = {
  'USA': '+1',
  'Canada': '+1',
  'UK': '+44',
  'India': '+91',
  'Australia': '+61',
  'New Zealand': '+64',
  'Singapore': '+65',
  'Japan': '+81',
  'UAE': '+971',
  'Germany': '+49',
  'Indonesia': '+62',
  'Kuwait': '+965',
  'Netherlands': '+31',
  'Oman': '+968',
  'Saudi Arabia': '+966',
  'Sweden': '+46'
};

const VALUE_SEP = '|';

/** Unique Select value so USA/Canada (+1) do not collide. Format: "+1|USA" */
export const toPhoneCodeSelectValue = (code, country) => `${code}${VALUE_SEP}${country}`;

/** Parse Select value or legacy dial-only string into { code, country }. */
export const parsePhoneCodeSelectValue = (value) => {
  if (!value) return { code: '+91', country: null };
  const str = String(value);
  if (str.includes(VALUE_SEP)) {
    const [code, country] = str.split(VALUE_SEP);
    return { code: code || '+91', country: country || null };
  }
  return { code: str, country: null };
};

/** Dial code only (for concatenating with phone number on save). */
export const getDialCodeFromSelectValue = (value) => parsePhoneCodeSelectValue(value).code;

/** Unique Select value for a country name. */
export const getPhoneCodeSelectValueForCountry = (country) => {
  if (country && phoneCodes[country]) {
    return toPhoneCodeSelectValue(phoneCodes[country], country);
  }
  return toPhoneCodeSelectValue('+91', 'India');
};

/**
 * Resolve unique Select value from dial code + optional country hint (edit prefill).
 * When dial is +1 and country is USA/Canada, that country wins; otherwise first match (USA before Canada).
 */
export const resolvePhoneCodeSelectValue = (dialCode, countryHint = '') => {
  const code = dialCode || '+91';
  if (countryHint && phoneCodes[countryHint] === code) {
    return toPhoneCodeSelectValue(code, countryHint);
  }
  const entry = Object.entries(phoneCodes).find(([, c]) => c === code);
  if (entry) {
    return toPhoneCodeSelectValue(entry[1], entry[0]);
  }
  if (countryHint && phoneCodes[countryHint]) {
    return getPhoneCodeSelectValueForCountry(countryHint);
  }
  return toPhoneCodeSelectValue('+91', 'India');
};

/** One country when the dial code is unique. +1 stays unresolved. */
export const uniqueCountryForE164 = (e164) => {
  const digits = String(e164 || '').replace(/\D/g, '');
  if (!digits) return null;
  const value = `+${digits}`;
  const codes = [...new Set(Object.values(phoneCodes))].sort((a, b) => b.length - a.length);
  const code = codes.find((item) => value.startsWith(item));
  if (!code) return null;
  const countries = Object.entries(phoneCodes)
    .filter(([, dial]) => dial === code)
    .map(([country]) => country);
  return countries.length === 1 ? countries[0] : null;
};

/** USA, Canada, and every other number whose dial code is +1. +91 stays India. */
export const isPlusOneNumber = (e164) => {
  const digits = String(e164 || '').replace(/\D/g, '');
  if (!digits) return false;
  const value = `+${digits}`;
  const codes = [...new Set(Object.values(phoneCodes))].sort((a, b) => b.length - a.length);
  const code = codes.find((item) => value.startsWith(item));
  return code === '+1';
};

// Get phone code for a country
export const getPhoneCode = (country) => {
  return phoneCodes[country] || '+91'; // Default to India if country not found
};

// Get all phone codes for dropdown (includes unique `value` for Select)
export const getAllPhoneCodes = () => {
  return Object.entries(phoneCodes).map(([country, code]) => ({
    country,
    code,
    value: toPhoneCodeSelectValue(code, country),
  }));
};
